import { Button } from "@mantine/core";
import type { CellContext } from "@tanstack/react-table";
import Link from "next/link";
import { type MouseEvent, type ReactNode, useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import type { TestID } from "@/utils/helpers";

export interface ButtonCellProps<TData> {
  buildHref?: (info: CellContext<TData, void>) => string;
  children: ReactNode;
  info: CellContext<TData, void>;
  leftSection?: ReactNode;
  onClick?: (info: CellContext<TData, void>, event: MouseEvent) => void;
  rightSection?: ReactNode;
  testId?: TestID;
}

export const ButtonCell = <TData,>({
  buildHref,
  children,
  info,
  leftSection,
  onClick,
  rightSection,
  testId,
}: ButtonCellProps<TData>) => {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      onClick?.(info, e);
    },
    [info, onClick],
  );

  const sharedProps = useMemo(() => {
    return {
      children,
      className: twMerge(["text-uw-purple", "text-xs", "uppercase"]),
      leftSection,
      onClick: handleClick,
      rightSection,
      ["data-testid"]: testId?.addDiscriminator(info.row.index).id,
      variant: "transparent",
    };
  }, [
    children,
    handleClick,
    info.row.index,
    leftSection,
    rightSection,
    testId,
  ]);

  if (buildHref) {
    return <Button component={Link} href={buildHref(info)} {...sharedProps} />;
  }

  return <Button {...sharedProps} />;
};
